import { useState } from "react";
import { toast } from "sonner";
import { CreditCard, Smartphone, Gift, Crown, ExternalLink } from "lucide-react";
import { createBillingPortal } from "@/lib/apiClient";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

const SOURCES = {
  website: { label: "Premium via website", icon: CreditCard, manage: "portal" },
  google_play: { label: "Premium via Google Play", icon: Smartphone, manage: "https://play.google.com/store/account/subscriptions" },
  app_store: { label: "Premium via App Store", icon: Smartphone, manage: "https://apps.apple.com/account/subscriptions" },
  app: { label: "Premium via the Lumivia app", icon: Smartphone, note: "Manage it in your phone's app store" },
  referral: { label: "Bonus Premium from referrals", icon: Gift },
  complimentary: { label: "Complimentary Premium", icon: Crown },
};

export const useBillingPortal = () => {
  const [loading, setLoading] = useState(false);
  const open = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { url } = await createBillingPortal({ origin_url: window.location.origin });
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the subscription portal.");
      setLoading(false);
    }
  };
  return { open, loading };
};

const Source = ({ s, portal }) => {
  const cfg = SOURCES[s.source] || SOURCES.app;
  const Icon = cfg.icon;
  const linkCls = "inline-flex items-center gap-1 text-xs text-teal-300 hover:text-teal-200 underline";
  return (
    <div data-testid={`premium-source-${s.source}`} className="flex items-start gap-2.5 py-1.5">
      <Icon className="h-4 w-4 text-teal-300 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-sm text-emerald-50">{cfg.label}</div>
        {s.until && <div className="text-xs text-stone-400">{s.source === "referral" ? "Until" : "Renews or ends"} {fmt(s.until)}</div>}
        {cfg.manage === "portal" && (
          <button data-testid="manage-website-subscription" onClick={portal.open} className={linkCls}>{portal.loading ? "Opening…" : "Manage subscription"}</button>
        )}
        {cfg.manage && cfg.manage !== "portal" && (
          <a data-testid={`manage-${s.source}-subscription`} href={cfg.manage} target="_blank" rel="noreferrer" className={linkCls}>Manage subscription <ExternalLink className="h-3 w-3" /></a>
        )}
        {cfg.note && <div className="text-xs text-stone-400">{cfg.note}</div>}
      </div>
    </div>
  );
};

export const PremiumSources = ({ sources, className = "" }) => {
  const portal = useBillingPortal();
  if (!sources?.length) return null;
  return (
    <div data-testid="premium-sources" className={className}>
      {sources.map((s) => <Source key={s.source} s={s} portal={portal} />)}
    </div>
  );
};
